import { Capacitor, registerPlugin } from "@capacitor/core";

export type NativePermissionState = "granted" | "denied" | "prompt" | "unsupported";

export interface PermissionStatusSummary {
  readonly location: NativePermissionState;
  readonly microphone: NativePermissionState;
}

export interface NativeBiometricStatus {
  readonly available: boolean;
  readonly biometricAvailable: boolean;
  readonly deviceSecure: boolean;
  readonly biometryType: "face" | "touch" | "optic" | "biometric" | "none" | "unknown";
}

export interface NativeSecurityStatus {
  readonly nativeAvailable: boolean;
  readonly permissions: PermissionStatusSummary;
  readonly biometric: NativeBiometricStatus;
  readonly appSettingsAvailable: boolean;
}

export interface DeviceOwnerAuthenticationResult {
  readonly authenticated: boolean;
  readonly unavailableReason?: "native_unavailable" | "not_authenticated";
}

interface PortabilitySecureSessionPlugin {
  getBiometricStatus(): Promise<NativeBiometricStatus>;
  authenticateDeviceOwner(options: { readonly explicitUserAction: true; readonly reason: string }): Promise<{ readonly authenticated: boolean }>;
  getPermissionStatus(): Promise<PermissionStatusSummary>;
  openAppSettings(): Promise<{ readonly opened: boolean }>;
}

const unsupportedPermissions: PermissionStatusSummary = Object.freeze({
  location: "unsupported",
  microphone: "unsupported"
});

const unsupportedBiometricStatus: NativeBiometricStatus = Object.freeze({
  available: false,
  biometricAvailable: false,
  deviceSecure: false,
  biometryType: "none"
});

const PortabilitySecureSession = registerPlugin<PortabilitySecureSessionPlugin>("PortabilitySecureSession");

export function nativeSecurityBridgeAvailable(): boolean {
  return Capacitor.isNativePlatform() && (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android");
}

function normalizePermissionState(value: unknown): NativePermissionState {
  return value === "granted" || value === "denied" || value === "prompt" || value === "unsupported" ? value : "unsupported";
}

function normalizePermissionStatus(value: PermissionStatusSummary): PermissionStatusSummary {
  return Object.freeze({
    location: normalizePermissionState(value.location),
    microphone: normalizePermissionState(value.microphone)
  });
}

function normalizeBiometryType(value: unknown): NativeBiometricStatus["biometryType"] {
  return value === "face" || value === "touch" || value === "optic" || value === "biometric" || value === "none" || value === "unknown" ? value : "unknown";
}

function normalizeBiometricStatus(value: NativeBiometricStatus): NativeBiometricStatus {
  return Object.freeze({
    available: value.available === true,
    biometricAvailable: value.biometricAvailable === true,
    deviceSecure: value.deviceSecure === true,
    biometryType: normalizeBiometryType(value.biometryType)
  });
}

export async function readNativeSecurityStatus(): Promise<NativeSecurityStatus> {
  if (!nativeSecurityBridgeAvailable()) {
    return Object.freeze({
      nativeAvailable: false,
      permissions: unsupportedPermissions,
      biometric: unsupportedBiometricStatus,
      appSettingsAvailable: false
    });
  }
  const [permissions, biometric] = await Promise.all([
    PortabilitySecureSession.getPermissionStatus(),
    PortabilitySecureSession.getBiometricStatus()
  ]);
  return Object.freeze({
    nativeAvailable: true,
    permissions: normalizePermissionStatus(permissions),
    biometric: normalizeBiometricStatus(biometric),
    appSettingsAvailable: true
  });
}

export async function authenticateDeviceOwnerForSessionLock(reason = "Unlock Portability"): Promise<DeviceOwnerAuthenticationResult> {
  if (!nativeSecurityBridgeAvailable()) return Object.freeze({ authenticated: false, unavailableReason: "native_unavailable" as const });
  try {
    const result = await PortabilitySecureSession.authenticateDeviceOwner({ explicitUserAction: true, reason });
    return Object.freeze(result.authenticated === true
      ? { authenticated: true }
      : { authenticated: false, unavailableReason: "not_authenticated" as const });
  } catch {
    return Object.freeze({ authenticated: false, unavailableReason: "not_authenticated" as const });
  }
}

export async function openNativeAppSettings(): Promise<boolean> {
  if (!nativeSecurityBridgeAvailable()) return false;
  try {
    return (await PortabilitySecureSession.openAppSettings()).opened === true;
  } catch {
    return false;
  }
}
